import { refreshBenefitQueries } from './benefitQueries';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
const token = () => localStorage.getItem('POSTPOP_AUTH_TOKEN');
export { benefitQuery } from './benefitQueries';
export function memberProgress(status, mark, milestones, now = Date.now()) {
  const months = Math.max(
    0,
    status?.supportActiveMonths ?? mark?.currentActiveMonths ?? mark?.currentPaidMonths ?? 0,
  );
  const next = milestones.find((m) => m.paidMonths > Math.max(months, 1));
  const date = new Date(status?.nicknameColorActiveUntil);
  const days =
    status?.nicknameColorActiveUntil && Number.isFinite(date.getTime())
      ? Math.max(0, Math.ceil((date.getTime() - now) / 86400000))
      : null;
  return {
    months,
    next,
    progress: next
      ? Math.max(0, Math.min(100, (months / next.paidMonths) * 100))
      : status?.isSupporter
        ? 100
        : 0,
    days,
  };
}
export default function useSupportMember(user, palette) {
  const client = useQueryClient();
  const identity = useUserStore((s) => `${s.user?.role}:${s.isAdmin}:${s.isLogin}`);
  const [generation, renew] = useState(0),
    [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false);
  const scope = useRef({
    alive: true,
    revision: 0,
    requests: new Set(),
    locked: false,
    token: token(),
  });
  const refreshCredential = () => {
    const s = scope.current;
    if (!s.alive || s.token === token()) return false;
    s.token = token();
    s.revision++;
    s.requests.forEach((c) => c.abort());
    s.locked = false;
    setBusy(false);
    setFailed(false);
    renew((v) => v + 1);
    return true;
  };
  useEffect(() => {
    const s = scope.current;
    s.alive = true;
    const cancel = () => {
      s.revision++;
      s.requests.forEach((r) => r.abort());
    };
    const off = useUserStore.subscribe((a, b) => {
      if (a.user?.id !== b.user?.id || a.isLogin !== b.isLogin || a.isAdmin !== b.isAdmin) cancel();
    });
    const check = () => refreshCredential();
    window.addEventListener('focus', check);
    window.addEventListener('storage', check);
    return () => {
      window.removeEventListener('focus', check);
      window.removeEventListener('storage', check);
      s.alive = false;
      cancel();
      off();
    };
  }, []);
  const key = ['sponsorships', 'me', user?.id ?? 'guest', identity, generation];
  const request = (signal) => {
    const s = scope.current,
      version = s.revision,
      credential = token(),
      c = new AbortController();
    s.requests.add(c);
    const abort = () => c.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) c.abort();
    const current = () =>
      s.alive &&
      !c.signal.aborted &&
      version === s.revision &&
      useUserStore.getState().user?.id === user?.id &&
      token() === credential;
    const finish = () => {
      signal?.removeEventListener('abort', abort);
      s.requests.delete(c);
      refreshCredential();
    };
    return {
      current,
      finish,
      options: {
        signal: c.signal,
        isSessionCurrent: current,
        suppressGlobalError: true,
        timeout: 15000,
      },
    };
  };
  const read = async (url, signal) => {
    const r = request(signal);
    try {
      const result = await api.getAuth(url, r.options);
      if (!r.current()) throw Error('Session changed');
      return result.data.data;
    } finally {
      r.finish();
    }
  };
  const status = useQuery({
    queryKey: key,
    enabled: !!user?.id,
    retry: false,
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const d = await read('/sponsorships/me', signal);
      if (typeof d?.isSupporter !== 'boolean') throw Error('Invalid support status');
      return d;
    },
  });
  const badges = useQuery({
    queryKey: ['badges', user?.id ?? 'guest', 'support-benefits', identity, generation],
    enabled: !!user?.id,
    retry: false,
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const d = await read('/me/badges', signal);
      if (!Array.isArray(d?.sponsorshipMark?.stages)) throw Error('Invalid support marks');
      return d.sponsorshipMark;
    },
  });
  const choose = async (selectedNicknameColorKey) => {
    const s = scope.current,
      previous = status.data;
    if (refreshCredential()) return;
    if (
      s.locked ||
      !user?.id ||
      useUserStore.getState().user?.id !== user.id ||
      !previous?.nicknameColorEntitled ||
      status.isError ||
      status.isFetching ||
      (selectedNicknameColorKey !== null &&
        !palette.some((p) => p.key === selectedNicknameColorKey))
    )
      return;
    s.locked = true;
    setBusy(true);
    setFailed(false);
    const r = request();
    const preferences = {
      ...previous.preferences,
      selectedNicknameColorKey,
      supporterRosterVisible:
        previous.preferences?.supporterRosterVisible ?? previous.adminBenefitsAccess ?? false,
    };
    client.setQueryData(key, { ...previous, preferences });
    let confirmed = false;
    try {
      const response = await api.putAuth('/sponsorships/me/preferences', preferences, r.options);
      if (!r.current()) return;
      const next = response.data.data;
      if (typeof next?.isSupporter !== 'boolean' || !next.preferences)
        throw Error('Invalid support response');
      confirmed = true;
      client.setQueryData(key, next);
      const color = next.nicknameColorEntitled
        ? (palette.find((p) => p.key === next.preferences.selectedNicknameColorKey) ?? null)
        : null;
      useUserStore.setState((st) => ({
        user: { ...st.user, profileMark: { ...st.user.profileMark, nicknameColor: color } },
      }));
      void refreshBenefitQueries(client, key);
      // Refresh identity under the same account/credential guard, never via unguarded fetchUser.
      const me = await api.getAuth('/me', r.options);
      if (r.current() && me.data.data?.id === user.id)
        useUserStore.setState((st) => ({ user: { ...st.user, ...me.data.data } }));
    } catch (e) {
      if (r.current()) {
        // A failed identity refresh must not undo a confirmed preference save.
        if (!confirmed) {
          client.setQueryData(key, previous);
          setFailed(true);
        }
      }
    } finally {
      r.finish();
      s.locked = false;
      if (s.alive) {
        setBusy(false);
      }
    }
  };
  return { status, badges, busy, failed, choose };
}
